import { useLocalVideo, useToggleLocalMute } from 'amazon-chime-sdk-component-library-react'
import { XIcon } from 'lucide-react'
import { VideoControlsGroup } from '@/components/molecules/VideoControlsGroup'
import { MeetingActionButton } from '@components/molecules/MeetingActionButton/MeetingActionButton'
import { useLeave } from '@hooks/useLeaveMeeting'
import { useDeviceSelection } from '@hooks/useDeviceSelection'

type CustomMeetingControlsProps = {
  isCameraPermissionDenied?: boolean
  isMicrophonePermissionDenied?: boolean
  mode: 'video' | 'phone'
}

export function CustomMeetingControls({
  isCameraPermissionDenied = false,
  isMicrophonePermissionDenied = false,
  mode
}: CustomMeetingControlsProps) {
  const { isVideoEnabled, toggleVideo } = useLocalVideo()
  const { muted, toggleMute } = useToggleLocalMute()
  const { leave, isLeaving } = useLeave(mode)

  const deviceSelection = useDeviceSelection({ autoSelect: false })

  const handleVideoToggle = async () => {
    if (isCameraPermissionDenied) return
    try {
      await toggleVideo()
    } catch (error) {
      console.error('Error toggling video:', error)
    }
  }

  const handleMicrophoneToggle = () => {
    if (isMicrophonePermissionDenied) return
    toggleMute()
  }

  const handleLeave = () => {
    if (!isLeaving) {
      leave()
    }
  }

  const videoEnabled = isCameraPermissionDenied ? false : isVideoEnabled
  const microphoneEnabled = isMicrophonePermissionDenied ? false : !muted

  return (
    <div className="flex items-center justify-center gap-4">
      <VideoControlsGroup
        className="gap-4"
        isVideoActive={videoEnabled}
        isMicrophoneActive={microphoneEnabled}
        onToggleVideo={handleVideoToggle}
        onToggleMicrophone={handleMicrophoneToggle}
        isCameraPermissionDenied={isCameraPermissionDenied}
        isMicrophonePermissionDenied={isMicrophonePermissionDenied}
        videoInputs={deviceSelection.videoInputs.map((d) => ({ id: d.id, label: d.label }))}
        audioInputs={deviceSelection.audioInputs.map((d) => ({ id: d.id, label: d.label }))}
        audioOutputs={deviceSelection.audioOutputs.map((d) => ({ id: d.id, label: d.label }))}
        selectedVideoInput={deviceSelection.selectedVideoInput}
        selectedAudioInput={deviceSelection.selectedAudioInput}
        selectedAudioOutput={deviceSelection.selectedAudioOutput}
        onVideoInputChange={(id) => void deviceSelection.selectVideoInput({ id })}
        onAudioInputChange={(id) => void deviceSelection.selectAudioInput({ id })}
        onAudioOutputChange={(id) => void deviceSelection.selectAudioOutput({ id })}
        buttonSize="lg"
      />
      <MeetingActionButton
        icon={XIcon}
        onClick={handleLeave}
        variant="destructive"
        disabled={isLeaving}
        buttonSize="lg"
      />
    </div>
  )
}
